import { useState, useCallback } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { usePerson, useDeletePerson } from '@/hooks/usePersons';
import { useTreeStore } from '@/store/useTreeStore';
import { useAuthStore } from '@/store/useAuthStore';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { PersonForm } from '@/components/PersonForm/PersonForm';
import { OverviewTab } from './tabs/OverviewTab';
import { RelationshipsTab } from './tabs/RelationshipsTab';
import { MediaTab } from './tabs/MediaTab';
import { SourcesTab } from './tabs/SourcesTab';
import { MedicalTab } from './tabs/MedicalTab';

// ─── Tab-Definitionen ───────────────────────────────────────────────────────

type TabId = 'overview' | 'relationships' | 'medical' | 'media' | 'sources';

const BASE_TABS: Array<{ id: TabId; label: string }> = [
  { id: 'overview', label: 'Übersicht' },
  { id: 'relationships', label: 'Beziehungen' },
  { id: 'media', label: 'Medien' },
  { id: 'sources', label: 'Quellen' },
];

// ─── PersonDetail ───────────────────────────────────────────────────────────

interface PersonDetailProps {
  personId: string;
}

export function PersonDetail({ personId }: PersonDetailProps) {
  const { data: person, isLoading, isError, error, refetch } = usePerson(personId);
  const deletePerson = useDeletePerson();
  const setSelectedPerson = useTreeStore((s) => s.setSelectedPerson);
  const confirm = useConfirm();

  // Bearbeiten/Löschen für alle mit persons.manage (nicht nur Admins) –
  // das Backend prüft dieselbe Berechtigung.
  const canManage = useAuthStore((s) => s.hasPermission('persons.manage'));
  // Medizin-Tab für Admins sowie Nutzer mit explizitem Medizin-Recht (Art. 9 DSGVO, auditiert).
  const canViewMedical = useAuthStore((s) => s.hasSensitivePermission('medical.view'));
  const TABS = canViewMedical
    ? [...BASE_TABS.slice(0, 2), { id: 'medical' as TabId, label: 'Medizin' }, ...BASE_TABS.slice(2)]
    : BASE_TABS;
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showEditModal, setShowEditModal] = useState(false);

  // ─── Löschen mit Bestätigung ──────────────────────────────────────────

  const handleDelete = useCallback(async () => {
    if (!person) return;

    const fullName = [person.title, person.firstName, person.lastName].filter(Boolean).join(' ');
    const confirmed = await confirm({
      title: 'Person löschen?',
      message: (
        <>
          <strong>"{fullName}"</strong> wirklich löschen? Diese Aktion kann nicht rückgängig
          gemacht werden. Alle zugehörigen Medien und Quellen werden ebenfalls gelöscht.
        </>
      ),
      confirmLabel: 'Löschen',
      cancelLabel: 'Abbrechen',
      variant: 'danger',
    });

    if (!confirmed) return;

    deletePerson.mutate(personId, {
      onSuccess: () => {
        setSelectedPerson(null);
      },
    });
  }, [person, personId, deletePerson, setSelectedPerson, confirm]);

  // ─── Loading / Error ──────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner message="Person wird geladen..." />
      </div>
    );
  }

  if (isError || !person) {
    return (
      <div className="p-4">
        <ErrorMessage
          message={error instanceof Error ? error.message : 'Person konnte nicht geladen werden.'}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col">
      {/* Tab-Navigation */}
      <div className="flex border-b border-slate-200">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 border-b-2 px-1 py-2.5 text-xs font-medium transition-colors
              ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab-Inhalt */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'overview' && <OverviewTab person={person} />}
        {activeTab === 'relationships' && <RelationshipsTab person={person} />}
        {activeTab === 'medical' && canViewMedical && <MedicalTab person={person} />}
        {activeTab === 'media' && <MediaTab personId={personId} />}
        {activeTab === 'sources' && <SourcesTab person={person} />}
      </div>

      {/* Aktions-Buttons unten (nur mit persons.manage) */}
      {canManage && (
        <div className="flex items-center gap-2 border-t border-slate-200 px-4 py-3">
          <button
            onClick={() => setShowEditModal(true)}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white
                       px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors
                       hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
            Bearbeiten
          </button>

          <button
            onClick={handleDelete}
            disabled={deletePerson.isPending}
            className="flex items-center justify-center gap-1.5 rounded-md border border-red-200 bg-white
                       px-3 py-2 text-sm font-medium text-red-600 shadow-sm transition-colors
                       hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
                       disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Löschen
          </button>
        </div>
      )}

      {/* Bearbeiten-Modal */}
      {showEditModal && (
        <PersonForm person={person} onClose={() => setShowEditModal(false)} />
      )}
    </div>
  );
}
